<template>
  <section class="lab-readings" aria-label="Readings at the selected time">
    <div class="readings-head">
      <h3>Readings at {{ time }}</h3>
      <span class="sub">{{ date }}</span>
      <span v-if="following && streaming" class="following"><span class="dot" aria-hidden="true"></span>Latest reading</span>
      <input
        v-model.trim="query"
        type="search"
        class="readings-search"
        placeholder="Find a parameter"
        aria-label="Find a parameter"
      />
    </div>

    <div v-if="!shown.length" class="readings-none">No parameter matches “{{ query }}”.</div>
    <div v-else class="readings-cols">
      <div v-for="group in shown" :key="group.name" class="rd-group">
        <h4><span>{{ group.name }}</span><span class="count">{{ group.channels.length }}</span></h4>
        <div
          v-for="c in group.channels"
          :key="c.key"
          :class="['rd-row', { inactive: !c.active }]"
          :title="c.active ? c.name : `${c.name}: nothing recorded on this channel`"
        >
          <span class="rd-name">{{ c.name }}</span>
          <span class="rd-val">{{ c.value }}</span>
          <span class="rd-unit">{{ c.unit }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
// Every channel of the test at one moment, like the lab software's data view:
// the rig's own groups, one card each. Channels that never recorded anything
// are kept (the list is the rig's) but dimmed.
export default {
  name: 'LabReadings',
  props: {
    // [{ name, channels: [{ key, name, unit, value, active }] }], values formatted
    groups: { type: Array, default: () => [] },
    time: { type: String, default: '' },
    date: { type: String, default: '' },
    following: { type: Boolean, default: false },
    streaming: { type: Boolean, default: false }
  },
  data () {
    return { query: '' }
  },
  computed: {
    // A group whose name matches keeps all its channels
    shown () {
      const q = this.query.toLowerCase()
      if (!q) return this.groups
      return this.groups
        .map(g => g.name.toLowerCase().includes(q)
          ? g
          : { ...g, channels: g.channels.filter(c => c.name.toLowerCase().includes(q)) })
        .filter(g => g.channels.length)
    }
  }
}
</script>

<style scoped lang="scss">
.lab-readings {
  flex: 1 0 auto;
  padding: 12px 20px 16px;
}

.readings-head {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px 12px;
  margin-bottom: 12px;

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: var(--lab-accent);
    font-variant-numeric: tabular-nums;
  }

  .sub {
    font-size: 12px;
    color: var(--lab-muted);
  }
}

.following {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: var(--lab-live);

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
}

.readings-search {
  margin-left: auto;
  width: 240px;
  padding: 6px 10px;
  font-size: 12px;
  color: var(--lab-text);
  background: var(--lab-surface);
  border: 1px solid var(--lab-border);
  border-radius: 4px;

  &:focus { outline: 2px solid #60a5fa; outline-offset: 0; border-color: transparent; }
}

// Cards flow down and across like newspaper columns, so groups of very
// different lengths pack without gaps; a phone gets one column.
.readings-cols {
  column-width: 290px;
  column-gap: 12px;
}

.rd-group {
  break-inside: avoid;
  margin-bottom: 12px;
  background: var(--lab-surface);
  border: 1px solid var(--lab-border);
  border-radius: 6px;
  overflow: hidden;

  h4 {
    display: flex;
    justify-content: space-between;
    margin: 0;
    padding: 7px 10px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--lab-accent);
    background: var(--lab-sidebar);
    border-bottom: 1px solid var(--lab-border);

    .count { color: var(--lab-muted); font-weight: 600; }
  }
}

.rd-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto 48px;
  gap: 8px;
  align-items: baseline;
  padding: 3px 10px;
  font-size: 12px;
  border-bottom: 1px solid #f0f1f3;

  &:last-child { border-bottom: none; }
  &.inactive { opacity: 0.45; }
}

.rd-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rd-val {
  font-weight: 700;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.rd-unit {
  font-size: 10px;
  color: var(--lab-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.readings-none {
  padding: 24px 0;
  color: var(--lab-muted);
}

@media (max-width: 640px) {
  .lab-readings { padding: 10px 12px 12px; }

  .readings-search {
    order: 5;
    margin-left: 0;
    width: 100%;
    padding: 9px 12px;
    font-size: 14px;
  }

  .rd-row {
    padding: 6px 12px;
    font-size: 13px;
  }
}
</style>
